import React, { useState, useEffect } from 'react';
import { TestPreguntaApi } from '../../api/TestPreguntaApi';
import './Quiz.css';

const LETTERS     = ['A', 'B', 'C'];
const OPTION_KEYS = ['opciona', 'opcionb', 'opcionc'];
const ANSWER_KEYS = ['a', 'b', 'c'];

// Returns 'a'|'b'|'c' regardless of whether answer is stored as letter or full text
const resolveCorrectLetter = (p) => {
  const raw = p.answer?.toLowerCase().trim();
  if (!raw) return null;
  // already a letter
  if (raw === 'a' || raw === 'b' || raw === 'c') return raw;
  // full text — find which option matches
  return ANSWER_KEYS.find((_, i) =>
    p[OPTION_KEYS[i]]?.toLowerCase().trim() === raw
  ) ?? null;
};

const quizApi = new TestPreguntaApi();

const Quiz = ({ modulo, onQuizComplete }) => {
  const [preguntas,    setPreguntas]    = useState([]);
  const [respuestas,   setRespuestas]   = useState({});   // { [preguntaId]: 'a'|'b'|'c' }
  const [submitted,    setSubmitted]    = useState(false);
  const [aciertos,     setAciertos]     = useState(0);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    if (!modulo) return;
    setLoading(true);
    setRespuestas({});
    setSubmitted(false);
    quizApi.getAllTestpregunta(modulo)
      .then(data => setPreguntas(Array.isArray(data) ? data : []))
      .catch(() => setPreguntas([]))
      .finally(() => setLoading(false));
  }, [modulo]);

  const select = (preguntaId, letter) => {
    if (submitted) return;
    setRespuestas(prev => ({ ...prev, [preguntaId]: letter }));
  };

  const answered = Object.keys(respuestas).length;

  const handleSubmit = () => {
    const total   = preguntas.length;
    let   correct = 0;
    preguntas.forEach(p => {
      if (respuestas[p.id] === resolveCorrectLetter(p)) correct++;
    });
    setAciertos(correct);
    setSubmitted(true);
    const passed = correct / total >= 0.7;
    onQuizComplete(passed);
  };

  if (loading) {
    return (
      <div className="quiz-loading">
        <div className="quiz-spinner" />
        <p>Cargando preguntas…</p>
      </div>
    );
  }

  if (preguntas.length === 0) {
    return (
      <div className="quiz-empty">
        <p>No hay preguntas disponibles para este módulo.</p>
      </div>
    );
  }

  const total  = preguntas.length;
  const passed = aciertos / total >= 0.7;

  return (
    <div className="quiz-wrap">

      {/* ── Header ── */}
      <div className="quiz-header">
        <div className="quiz-header-left">
          <span className="quiz-tag">Test del módulo</span>
          <h2 className="quiz-title">Demuestra lo que sabes</h2>
        </div>
        {!submitted && (
          <div className="quiz-counter">
            <span className="quiz-counter-num">{answered}</span>
            <span className="quiz-counter-sep">/</span>
            <span className="quiz-counter-total">{total}</span>
          </div>
        )}
      </div>

      {/* ── Progress bar ── */}
      {!submitted && (
        <div className="quiz-progress">
          <div
            className="quiz-progress-fill"
            style={{ width: total ? `${(answered / total) * 100}%` : '0%' }}
          />
        </div>
      )}

      {/* ── Result banner ── */}
      {submitted && (
        <div className={`quiz-result ${passed ? 'quiz-result--pass' : 'quiz-result--fail'}`}>
          <span className="quiz-result-icon">{passed ? '🏆' : '📚'}</span>
          <div>
            <div className="quiz-result-title">
              {passed ? '¡Módulo superado!' : 'Sigue practicando'}
            </div>
            <div className="quiz-result-sub">
              {aciertos} de {total} correctas
              {passed
                ? ' — Has desbloqueado el siguiente módulo.'
                : ' — Necesitas al menos el 70% para aprobar.'}
            </div>
          </div>
        </div>
      )}

      {/* ── Questions ── */}
      <div className="quiz-questions">
        {preguntas.map((p, qi) => {
          const userAnswer    = respuestas[p.id];
          const correctLetter = resolveCorrectLetter(p);

          return (
            <div key={p.id} className="quiz-q">
              <div className="quiz-q-head">
                <span className="quiz-q-num">{qi + 1}</span>
                <p className="quiz-q-text">{p.pregunta}</p>
              </div>

              <div className="quiz-options">
                {ANSWER_KEYS.map((letter, oi) => {
                  const optionText = p[OPTION_KEYS[oi]];
                  const isSelected = userAnswer === letter;
                  const isCorrect  = letter === correctLetter;

                  let mod = '';
                  if (submitted) {
                    if (isCorrect)                     mod = 'correct';
                    else if (isSelected && !isCorrect) mod = 'wrong';
                  } else if (isSelected) {
                    mod = 'selected';
                  }

                  return (
                    <button
                      key={letter}
                      type="button"
                      className={`quiz-option ${mod ? `quiz-option--${mod}` : ''}`}
                      onClick={() => select(p.id, letter)}
                      disabled={submitted}
                    >
                      <span className="quiz-option-letter">{LETTERS[oi]}</span>
                      <span className="quiz-option-text">{optionText}</span>
                      {submitted && isCorrect && (
                        <span className="quiz-option-check">✓</span>
                      )}
                      {submitted && isSelected && !isCorrect && (
                        <span className="quiz-option-x">✗</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Submit ── */}
      {!submitted && (
        <button
          className="quiz-submit"
          onClick={handleSubmit}
          disabled={answered < total}
        >
          {answered < total
            ? `Responde todas las preguntas (${answered}/${total})`
            : 'Enviar respuestas'}
        </button>
      )}
    </div>
  );
};

export default Quiz;
